import googleLibphonenumber from 'google-libphonenumber';
import { GraphQLError } from 'graphql/error';
import { sql } from 'kysely';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import uniqueId from 'lodash/uniqueId';
import upperFirst from 'lodash/upperFirst';
import values from 'lodash/values';
import PHPUnserialize from 'php-unserialize';
import { match, P } from 'ts-pattern';
import * as z from 'zod';
import type { RequestContainer } from '$lib/container/container.server';
import { utcDateToCalendarDate, utcDateToZonedDateTime } from '$lib/kysely/kysely-utils';
import { keepIsOk } from '$lib/utils/filterNotNull';
import { gravatarUrl } from '$lib/utils/gravatarUrl.js';
import { nullIfEmptyString } from '$lib/utils/nullIfEmptyString';
import { allValues } from '$lib/utils/promise';
import * as Result from '$lib/utils/result';
import { safe } from '$lib/utils/string.js';
import type { PartialOnUndefined } from '$lib/utils/types';
import { safeParse } from '$lib/utils/zod';
import type {
    ParticipantInput,
    ParticipantParticipantMediaForSlotArgs,
    QueryUserArgs,
    SexEnum,
    UserPhotoStatusEnum,
} from '$gql/resolver-types.js';
import { FloatWithCurrencyRef } from '$gql/resolvers/FloatWithCurrencyRef';
import type { MediaRef } from '$gql/resolvers/MediaRef';
import {
    inboxNbUnreadResolve,
    type ParticipantRef,
} from '$gql/resolvers/ParticipantRef.js';
import {
    zCareerStatusEnum,
    zEducationLevelEnum,
    zExperienceLevelEnum,
    zJobStatusEnum,
    zSexEnum,
    zUserMediaSlotEnum,
    zUserPhotoStatusEnum,
} from '$gql/zod-generated';
import type { PortfolioMediaRef } from './PortfolioMediaRef';

const { PhoneNumberUtil, PhoneNumberFormat: PNF } = googleLibphonenumber;

const ID = uniqueId('UserResolver');

export class UserRef {
    constructor(
        private readonly _context: Pick<
            RequestContainer,
            'db' | 'dataLoaderRegistry' | 'urlHelper' | 'refs' | 'securityManager'
        >,
        private readonly _id: number,
        private readonly _options: { security: boolean } = { security: true },
    ) {}

    __typename = 'User' as const;

    toParticipantInput(): ParticipantInput {
        return { companyId: null, userId: this._id };
    }

    async load() {
        return this._context.dataLoaderRegistry().user().load(this._id);
    }

    ifNotExistParticipantReturnNull(this: this): Promise<ParticipantRef | null> {
        return this.ifNotExistReturnNull();
    }

    async ifNotExistReturnNull(this: this): Promise<this | null> {
        try {
            await this.load();
            return this;
        } catch {
            return null;
        }
    }

    id() {
        return this._id;
    }

    async address() {
        return (await this.load()).address;
    }

    async birthdayDate() {
        const date = (await this.load()).birthday_date;
        return date === null ? null : utcDateToCalendarDate(date);
    }

    async confirmationToken(): Promise<string | null> {
        return (await this.load()).confirmation_token;
    }

    async category() {
        return this._context.refs().categoryOrNull((await this.load()).category_id);
    }

    async city() {
        return nullIfEmptyString((await this.load()).city);
    }

    async company() {
        return this._context.refs().companyOrNull((await this.load()).company_id);
    }

    async country() {
        return nullIfEmptyString((await this.load()).country);
    }

    async district() {
        await this.canManageUserOrThrow();

        return nullIfEmptyString((await this.load()).district);
    }

    async email() {
        return nullIfEmptyString((await this.load()).email);
    }

    async emailsValidationSent() {
        const rows = await this._context
            .db()
            .selectFrom('user_email_validation_sent')
            .selectAll()
            .where('user_id', '=', this._id)
            .execute();

        return rows.map((r) => utcDateToZonedDateTime(r.date));
    }

    async formationsCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-formationsCount`,
            async (userIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('user_formation')
                    .select(({ fn }) => ['user_id', fn.count<number>('id').as('count')])
                    .where('user_id', 'in', userIds)
                    .groupBy('user_id')
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => `${r.user_id ?? ''}`);
                return userIds.map((userId) => rowsByUserId[`${userId}`]?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async portfolioMedias(): Promise<PortfolioMediaRef[]> {
        const res = await this._context
            .db()
            .selectFrom('portfolio_media')
            .select('id')
            .where('user_id', '=', this._id)
            .orderBy('rank', 'asc')
            .execute();

        return Promise.all(res.map(({ id }) => this._context.refs().portfolioMedia(id)));
    }

    //NOTE: not sure if better to call portfolioMedias and count the length of the array, or to do a count query directly in the DB
    async portfolioMediasCount() {
        const portfolioMedias = await this._context
            .db()
            .selectFrom('portfolio_media')
            .select(({ fn }) => [
                'user_id',
                fn.count<number>('id').as('portfolio_medias_count'),
            ])
            .where('user_id', '=', this._id)
            .groupBy('user_id')
            .execute();

        return portfolioMedias[0]?.portfolio_medias_count ?? 0;
    }

    async experiencesCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-experiencesCount`,
            async (userIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('user_experience')
                    .select(({ fn }) => ['user_id', fn.count<number>('id').as('count')])
                    .where('user_id', 'in', userIds)
                    .groupBy('user_id')
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => `${r.user_id ?? ''}`);
                return userIds.map((userId) => rowsByUserId[`${userId}`]?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async resumesCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-resumesCount`,
            async (userIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('user_resume')
                    .select(({ fn }) => [
                        'user_id',
                        fn.count<number>('id').as('resumes_count'),
                    ])
                    .where('user_id', 'in', userIds)
                    .groupBy('user_id')
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => r.user_id);
                return userIds.map(
                    (userId) => rowsByUserId[`${userId}`]?.resumes_count ?? 0,
                );
            },
        );

        return await loader.load(this._id);
    }

    async enabled() {
        await this.canManageUserOrThrow();

        return Boolean((await this.load()).enabled);
    }

    async facebookId() {
        return (await this.load()).facebook_id;
    }

    async firstname() {
        return (await this.load()).firstname;
    }

    async fullname() {
        const { id, lastname, firstname } = await this.load();
        const fullname =
            `${upperFirst(firstname ?? '')} ${upperFirst(lastname ?? '')}`.trim();

        return fullname === '' ? safe`${id}` : fullname;
    }

    async gravatarUrl() {
        const { email } = await this.load();
        return gravatarUrl(email ?? '', {
            default: 'identicon',
        });
    }

    async lastLogin() {
        return (await this.load()).last_login;
    }

    async lastname() {
        return (await this.load()).lastname;
    }

    async level() {
        return (await this.load()).level;
    }

    async preferredLang() {
        return nullIfEmptyString((await this.load()).preferred_lang);
    }

    async profileBgPhoto() {
        return this._context.refs().mediaOrNull((await this.load()).profile_bg_photo_id);
    }

    async profileBgPhotoStatus(): Promise<UserPhotoStatusEnum> {
        const status = (await this.load()).profile_bg_photo_status;
        return Result.getWithDefault(safeParse(zUserPhotoStatusEnum, status), 'no');
    }

    async profileDescription() {
        return (await this.load()).profile_description;
    }

    async profilePhoto() {
        return this._context.refs().mediaOrNull((await this.load()).profile_photo_id);
    }

    async profilePhotoStatus() {
        const status = (await this.load()).profile_photo_status;
        return Result.getWithDefault(safeParse(zUserPhotoStatusEnum, status), 'no');
    }

    async profileTitle() {
        return (await this.load()).profile_title;
    }

    async registerBy() {
        await this.canManageUserOrThrow();

        return (await this.load()).register_by;
    }

    async registrationDate() {
        return (await this.load()).registration_date;
    }

    async sex(): Promise<SexEnum | null> {
        await this.canManageUserOrThrow();

        return Result.getWithDefault(safeParse(zSexEnum, (await this.load()).sex), null);
    }

    async subCity() {
        await this.canManageUserOrThrow();

        return nullIfEmptyString((await this.load()).sub_city);
    }

    async username() {
        return (await this.load()).username;
    }

    async zipcode() {
        await this.canManageUserOrThrow();

        return nullIfEmptyString((await this.load()).zipcode);
    }

    async inboxNbUnread(): Promise<number> {
        return inboxNbUnreadResolve(this._context, this);
    }

    async participantName() {
        return await this.fullname();
    }

    async participantMediaForSlot(
        args: PartialOnUndefined<ParticipantParticipantMediaForSlotArgs>,
    ): Promise<MediaRef | null> {
        const slot = zUserMediaSlotEnum.safeParse(args.slot);
        return await match(slot.data)
            .with('profile', () => this.profilePhoto())
            .with('profileBg', () => this.profileBgPhoto())
            .with(P.nullish, async () => null)
            .exhaustive();
    }

    async participantPhoto() {
        const { profile_photo_id, profile_photo_status } = await this.load();
        return profile_photo_status === 'valid' && profile_photo_id != null
            ? this._context.refs().media(profile_photo_id)
            : null;
    }

    async participantProfileLink() {
        const { firstname, lastname, country } = await this.load();

        return (await this._context.urlHelper()).userProfile({
            id: this.id(),
            firstname,
            lastname,
            country,
        });
    }

    async participantTitle() {
        return (await this.load()).profile_title;
    }

    async participantCountry() {
        return this.country();
    }

    async participantCity() {
        return this.city();
    }

    async manageThisCompany(companyId: number) {
        const row = await this._context
            .db()
            .selectFrom('company_manager')
            .where('company_id', '=', companyId)
            .where('user_id', '=', this.id())
            .select('id')
            .limit(1)
            .executeTakeFirst();

        return row?.id != null;
    }

    async managedCompanies() {
        await this.canManageUserOrThrow();

        const managers = await this._context
            .dataLoaderRegistry()
            .companyManager()
            .loadByUserId(this._id);

        return managers.map((m) => this._context.refs().companyManager(m.id));
    }

    async roles(): Promise<string[]> {
        await this.canManageUserOrThrow();

        const roles = z
            .array(z.string())
            .or(z.record(z.string().or(z.number()), z.string()))
            .safeParse(PHPUnserialize.unserialize((await this.load()).roles));

        if (!roles.success) {
            return ['ROLE_USER'];
        }

        return uniq(['ROLE_USER', ...values(roles.data)]);
    }

    async groupRoles(): Promise<string[]> {
        const { db } = this._context;

        await this.canManageUserOrThrow();

        const groups = await db()
            .selectFrom('fos_group')
            .select('fos_group.roles')
            .innerJoin('user_group', 'user_group.group_id', 'fos_group.id')
            .where('user_group.user_id', '=', this.id())
            .execute();

        return uniq(
            groups.flatMap((row) => {
                try {
                    return values(
                        Result.getWithDefault(
                            safeParse(
                                z
                                    .array(z.string())
                                    .or(z.record(z.string().or(z.number()), z.string())),
                                PHPUnserialize.unserialize(row.roles),
                            ),
                            [],
                        ),
                    );
                } catch {
                    return [];
                }
            }),
        );
    }

    async feature() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-feature`,
            async (userIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('user_feature')
                    .selectAll()
                    .where('user_id', 'in', userIds)
                    .groupBy('user_id')
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => r.user_id);
                return userIds.map((userId) => rowsByUserId[`${userId}`]);
            },
        );

        return await loader.load(this._id);
    }

    async followedByMe() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();
        const userId = await this._context.securityManager().currentUserIdOrNull();

        if (userId == null) return false;

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-followedByMe`,
            async (userIds: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .selectAll()
                    .where('author_id', '=', userId)
                    .where(({ and, eb }) =>
                        and([
                            eb('target_company_id', 'is', null),
                            eb('target_user_id', 'in', userIds),
                        ]),
                    )
                    .groupBy('target_user_id')
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => r.target_user_id ?? '');
                return userIds.map((userId) => rowsByUserId[`${userId}`]);
            },
            { batchScheduleFn: (cb) => setTimeout(cb, 10) },
        );

        return (await loader.load(this._id)) != null;
    }

    async feedPostsCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-feedPostsCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('feed_post')
                    .select(({ fn }) => [
                        'user_author_id',
                        fn.count<number>('id').as('count'),
                    ])
                    .where(({ and, eb }) =>
                        and([
                            eb('company_author_id', 'is', null),
                            eb('user_author_id', 'in', ids),
                        ]),
                    )
                    .groupBy('user_author_id')
                    .execute();

                const rowsById = keyBy(rows, (r) => `${r.user_author_id ?? ''}`);
                return ids.map((id) => rowsById[`${id}`]?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async followersCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-followersCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .select(({ fn }) => [
                        'target_user_id',
                        fn.count<number>('id').as('count'),
                    ])
                    .where(({ and, eb }) =>
                        and([
                            eb('target_company_id', 'is', null),
                            eb('target_user_id', 'in', ids),
                        ]),
                    )
                    .groupBy('target_user_id')
                    .execute();

                const rowsById = keyBy(rows, (r) => `${r.target_user_id ?? ''}`);
                return ids.map((id) => rowsById[`${id}`]?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async followingCount() {
        const db = this._context.db();
        const dataLoaderRegistry = this._context.dataLoaderRegistry();

        const loader = dataLoaderRegistry.createForRequest(
            `${ID}-followingCount`,
            async (ids: readonly number[]) => {
                const rows = await db
                    .selectFrom('following')
                    .select(({ fn }) => ['author_id', fn.count<number>('id').as('count')])
                    .where('author_id', 'in', ids)
                    .groupBy('author_id')
                    .execute();

                const rowsById = keyBy(rows, (r) => `${r.author_id ?? ''}`);
                return ids.map((id) => rowsById[`${id}`]?.count ?? 0);
            },
        );

        return await loader.load(this._id);
    }

    async hasAccessAdmin() {
        const { securityManager } = this._context;
        return await securityManager().userHasRole('ROLE_SONATA_ADMIN', this);
    }

    async participantOnline() {
        try {
            const row = await this._context
                .dataLoaderRegistry()
                .inboxOnline()
                .loadByUserId(this.id());

            return this._context.refs().inboxOnline({ id: row.id });
        } catch {
            return null;
        }
    }

    async firstPhoneNumber(this: this) {
        const { db, dataLoaderRegistry } = this._context;
        const phoneUtil = PhoneNumberUtil.getInstance();

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-firstPhoneNumber`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_coordinate')
                    .innerJoin('user as u', 'u.id', 'user_coordinate.user_id')
                    .select([
                        'user_id',
                        'type',
                        'content',
                        'phone_area_code',
                        'u.country',
                    ])
                    .where('user_id', 'in', userIds)
                    .where('type', 'in', ['tel', 'gsm'])
                    .groupBy(['user_id', 'type', 'content'])
                    .execute();

                const rowsByUserId = keyBy(rows, (r) => `${r.user_id}`);

                return userIds.map((userId) => {
                    const row = rowsByUserId[`${userId}`];

                    if (row == null || row.content == null) {
                        return null;
                    }

                    const countryUser = row.country;
                    let phoneAreaCode = row.phone_area_code;

                    let defaultCountry: string = 'ZZ';
                    if (phoneAreaCode != null) {
                        try {
                            defaultCountry = phoneUtil.getRegionCodeForCountryCode(
                                parseInt(phoneAreaCode, 10),
                            );
                        } catch {
                            /* empty */
                        }
                    } else if (countryUser != null) {
                        defaultCountry = countryUser.toUpperCase();
                    }

                    try {
                        const parsed = phoneUtil.parse(row.content, defaultCountry);

                        return phoneUtil.format(parsed, PNF.INTERNATIONAL);
                    } catch {
                        return row.content;
                    }
                });
            },
        );

        return loader.load(this.id());
    }

    async profileUrl() {
        const urlHelper = (await this._context.urlHelper()).withAbsoluteUrl(true);
        const { id, firstname, lastname, country } = await this.load();
        return urlHelper.userProfile({ id, firstname, lastname, country });
    }

    async jobStatus() {
        const { db, dataLoaderRegistry } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-jobStatus`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_job_status')
                    .select(['user_id', 'status'])
                    .where('user_id', 'in', userIds)
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.status);
                });
            },
        );

        const jobStatusList = await loader.load(this.id());

        return keepIsOk(
            jobStatusList.map((jobStatus) => safeParse(zJobStatusEnum, jobStatus)),
        );
    }

    async careerStatus() {
        const { career_status } = await this.load();
        return Result.getWithDefault(safeParse(zCareerStatusEnum, career_status), null);
    }

    async educationLevel() {
        const level = (await this.load()).education_level;
        return Result.getWithDefault(safeParse(zEducationLevelEnum, level), null);
    }

    async professionalExperienceYears() {
        const level = (await this.load()).professional_experience_years;
        return Result.getWithDefault(safeParse(zExperienceLevelEnum, level), null);
    }

    async openedToRemote() {
        return (await this.load()).opened_to_remote === 1;
    }

    async hideOpenedToWork() {
        return (await this.load()).hide_opened_to_work === 1;
    }

    async interestedInCategories(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-interestedInCategories`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_interested_category')
                    .select(['user_id', 'category_id'])
                    .where('user_id', 'in', userIds)
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.category_id);
                });
            },
        );

        const categoryIds = await loader.load(this.id());
        return categoryIds.map((id) => refs().category(id));
    }

    async hasCv() {
        return (await this.resumesCount()) >= 1;
    }

    async hasExperiences() {
        return (await this.experiencesCount()) >= 1;
    }

    async hasFormations() {
        return (await this.formationsCount()) >= 1;
    }

    async hasVideo() {
        return false;
    }

    async adminEditUrl() {
        return (await this._context.urlHelper()).userAdminEditUrl(this.id());
    }

    async meIsGrantedEdit() {
        return await this._context
            .securityManager()
            .userHasSomeRoles(
                ['ROLE_GOAFRICA_USER_ADMIN_USER_EDIT', 'ROLE_SUPER_ADMIN'],
                'current',
            );
    }

    async formations(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-formations`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_formation')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .orderBy('rank asc')
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const formationIds = await loader.load(this.id());
        return formationIds.map((id) => refs().userFormation(id));
    }

    async resumes(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-resumes`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_resume')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const resumeIds = await loader.load(this.id());
        return resumeIds.map((id) => refs().userResume(id));
    }

    async experiences(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-experiences`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_experience')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .orderBy('rank asc')
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const experienceIds = await loader.load(this.id());
        return experienceIds.map((id) => refs().userExperience(id));
    }

    async skills(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-skills`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_skill')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const skillIds = await loader.load(this.id());
        return skillIds.map((id) => refs().userSkill(id));
    }

    async coordinates(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        await this.canManageUserOrThrow();

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-coordinates`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_coordinate')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .where('type', '!=', '')
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const coordinateIds = await loader.load(this.id());
        return coordinateIds.map((id) => refs().userCoordinate(id));
    }

    async languages(this: this) {
        const { db, dataLoaderRegistry, refs } = this._context;

        const loader = dataLoaderRegistry().createForRequest(
            `${ID}-languages`,
            async (userIds: readonly number[]) => {
                const rows = await db()
                    .selectFrom('user_lang')
                    .select(['user_id', 'id'])
                    .where('user_id', 'in', userIds)
                    .execute();

                const rowsByUserId = groupBy(rows, (r) => `${r.user_id}`);
                return userIds.map((userId) => {
                    return (rowsByUserId[`${userId}`] ?? []).map((r) => r.id);
                });
            },
        );

        const languageIds = await loader.load(this.id());
        return languageIds.map((id) => refs().userLanguage(id));
    }

    async emailConfirmed() {
        return (await this.load()).confirmation_token == null;
    }

    async responseRate() {
        const { db } = this._context;
        const row = await db()
            .with('threads_with_me(thread_id)', (db) =>
                db
                    .selectFrom('inbox_thread_user as tu')
                    .innerJoin('inbox_thread as t', 't.id', 'tu.inbox_thread_id')
                    .select('tu.inbox_thread_id as thread_id')
                    .distinct()
                    .where('tu.user_id', '=', this._id)
                    .where('t.user_creator_id', '!=', this._id),
            )
            .with('threads_with_response(thread_id)', (db) =>
                db
                    .selectFrom('threads_with_me as t')
                    .select('t.thread_id')
                    .where((eb) =>
                        eb.exists(
                            eb
                                .selectFrom('inbox_message as m')
                                .selectAll()
                                .where('m.user_author_id', '=', this._id)
                                .where('m.thread_id', '=', eb.ref('t.thread_id')),
                        ),
                    ),
            )
            .selectFrom('threads_with_me as t')
            .select((eb) => [
                eb.fn.countAll().as('total'),
                eb
                    .selectFrom('threads_with_response')
                    .select((eb) => eb.fn.countAll().as('resp'))
                    .as('resp'),
            ])
            .limit(1)
            .executeTakeFirst();

        const total = parseInt((row?.total ?? 0).toString());
        const resp = parseInt((row?.resp ?? 0).toString());

        const rate = Math.round((resp * 100) / total);

        return Number.isFinite(rate) ? rate : null;
    }

    async responseTimeInMinutes() {
        const { db } = this._context;
        const row = await db()
            .with('threads_with_me(thread_id, created_date)', (db) =>
                db
                    .selectFrom('inbox_thread_user as tu')
                    .innerJoin('inbox_thread as t', 't.id', 'tu.inbox_thread_id')
                    .select(['tu.inbox_thread_id as thread_id', 't.created_date'])
                    .distinct()
                    .where('tu.user_id', '=', this._id)
                    .where('t.user_creator_id', '!=', this._id),
            )
            .with('threads_delay(thread_id, delay)', (db) =>
                db.selectFrom('threads_with_me as t').select((eb) => [
                    't.thread_id',
                    eb
                        .selectFrom('inbox_message as m')
                        .select((eb) =>
                            eb
                                .fn<number>('TIMESTAMPDIFF', [
                                    sql`MINUTE`,
                                    't.created_date',
                                    'm.created_date',
                                ])
                                .as('delay'),
                        )
                        .where('m.user_author_id', '=', this._id)
                        .where('m.thread_id', '=', eb.ref('t.thread_id'))
                        .where((eb) =>
                            eb(
                                'm.id',
                                '=',
                                eb
                                    .selectFrom('inbox_message')
                                    .select((eb) => eb.fn.min<number>('id').as('id'))
                                    .where(
                                        'inbox_message.thread_id',
                                        '=',
                                        eb.ref('t.thread_id'),
                                    )
                                    .where('inbox_message.user_author_id', '=', this._id),
                            ),
                        )
                        .as('delay'),
                ]),
            )
            //virer le plus long delay
            .with('delays_filtered(thread_id, delay)', (db) =>
                db
                    .selectFrom('threads_delay')
                    .selectAll()
                    .where('delay', 'is not', null)
                    .where((eb) =>
                        eb(
                            'delay',
                            '<',
                            eb
                                .selectFrom('threads_delay')
                                .select('delay')
                                .where('delay', 'is not', null)
                                .orderBy('delay desc')
                                .limit(1),
                        ),
                    ),
            )
            .selectFrom('delays_filtered as t')
            .select((eb) => [eb.fn.avg('t.delay').as('mean')])
            .limit(1)
            .executeTakeFirst();

        /** Virer les 3 plus grands
         SELECT AVG(score) AS average_score
         FROM scores
         WHERE score NOT IN (
         SELECT score
         FROM scores
         ORDER BY score DESC
         LIMIT 3
         );
         */

        const mean = row?.mean ?? null;
        return mean == null ? null : parseInt(mean.toString());
    }

    async indicativePrice() {
        const { indicative_price, indicative_price_currency } = await this.load();
        if (indicative_price != null && indicative_price_currency != null) {
            return new FloatWithCurrencyRef(indicative_price, indicative_price_currency);
        }

        return null;
    }

    async groups() {
        await this.canManageUserOrThrow();

        const { db, refs } = this._context;
        const rows = await db()
            .selectFrom('user_group')
            .select('group_id')
            .where('user_id', '=', this.id())
            .execute();

        return rows.map((r) => refs().group(r.group_id));
    }

    async telephone() {
        const coords = await this.coordinates();
        for (const coord of coords) {
            const { type, content, phoneAreaCode } = await allValues({
                type: coord.type(),
                content: coord.content(),
                phoneAreaCode: coord.phoneAreaCode(),
            });

            if (type === 'gsm' || type === 'tel') {
                const phoneUtil = PhoneNumberUtil.getInstance();
                try {
                    const parsed = phoneUtil.parse(
                        `${phoneAreaCode != null ? `+${phoneAreaCode}` : ''} ${content}`,
                        (await this.country()) ?? 'ZZ',
                    );
                    return phoneUtil.format(parsed, PNF.INTERNATIONAL);
                } catch {
                    return content;
                }
            }
        }

        return null;
    }

    private async canManageUserOrThrow() {
        const { securityManager } = this._context;

        if (
            this._options.security ||
            (await securityManager().userIsUserOrSuperAdmin(this.id(), 'current')) ||
            (await securityManager().userHasRole(
                'ROLE_GOAFRICA_ADMIN_USER_VIEW',
                'current',
            ))
        ) {
            return;
        } else {
            securityManager().throwSecurityMessage();
        }
    }

    async sequenceMail() {
        await this.canManageUserOrThrow();

        const { db, refs } = this._context;
        const row = await db()
            .selectFrom('sequence_mail__user_sequence')
            .select('sequence_mail__user_sequence.id')
            .where('user_id', '=', this.id())
            .limit(1)
            .executeTakeFirst();

        return row == null ? null : refs().sequenceMail_userSequence(row.id);
    }

    async notificationSettings() {
        await this.canManageUserOrThrow();

        return this._context.refs().userNotificationSettings(this._id);
    }
}

export class QueryUserRef {
    constructor(
        private readonly _context: Pick<RequestContainer, 'refs' | 'securityManager'>,
    ) {}

    user(this: this, args: PartialOnUndefined<QueryUserArgs>) {
        return this._context.refs().user(args.id);
    }

    async me(this: this) {
        const userId = await this._context.securityManager().currentUserIdOrNull();

        return this._context.refs().userOrNull(userId);
    }

    async meOrThrow(this: this) {
        const userId = await this._context.securityManager().currentUserIdOrNull();

        const user = await this._context
            .refs()
            .userOrNull(userId)
            ?.ifNotExistReturnNull();

        if (user == null) {
            throw new GraphQLError('You must be logged in');
        }

        return user;
    }
}
